<template>
  <div>
    <div class="d-flex justify-end mb-4">
      <v-text-field
        id="log-unitizacao-upload-search"
        v-model="search"
        append-icon="mdi-magnify"
        class="break-search"
        label="Procurar"
        hide-details
        single-line
        style="max-width: 250px;"
      />
    </div>

    <general-progress-bars
      class="mb-4"
      :items="downloadProgressData"
      prefix-text="Baixando relatório do upload"
    />

    <v-data-table
      class="log-unitizacao-table"
      :headers="headers"
      :items="itemsFormatados"
      :search.sync="search"
      :header-props="{ sortByText: 'Ordenar Por' }"
      :footer-props="{ itemsPerPageText: 'Itens por Página' }"
      no-data-text="Nenhum upload de Log de Unitização disponível"
      no-results-text="Nenhum registro correspondente encontrado"
      :loading="loading"
      mobile-breakpoint="800"
    >
      <template v-slot:[`item.nome`]="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <div
              class="text-truncate-column"
              v-bind="attrs"
              v-on="on"
            >
              {{ item.nome }}
            </div>
          </template>
          <span>{{ item.nome }}</span>
        </v-tooltip>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <general-status :status="item.status" />
      </template>

      <template v-slot:[`item.valor_total_formatado`]="{ item }">
        {{ item.valor_total_formatado }}
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-btn
          id="action-download-log-unitizacao"
          :disabled="!canDownload(item)"
          :class="!canDownload(item) ? 'cursor-block' : ''"
          min-width="0"
          class="px-1"
          fab
          icon
          x-small
          :loading="downloadProgressData[item.id] !== undefined"
          @click="downloadItem(item)"
        >
          <v-icon small>
            mdi-download
          </v-icon>
        </v-btn>

        <v-btn
          v-if="canDelete(item)"
          id="action-delete-log-unitizacao"
          color="white"
          class="ml-1"
          height="22px"
          width="22px"
          fab
          x-small
          elevation="1"
          @click="openDialogDelete(item)"
        >
          <v-icon small>
            mdi-trash-can-outline
          </v-icon>
        </v-btn>

        <v-btn
          id="action-details-log-unitizacao"
          color="white"
          class="ml-1"
          height="22px"
          width="22px"
          fab
          x-small
          elevation="1"
          @click="openDialogDetalhes(item)"
        >
          <v-icon small>
            mdi-file-find-outline
          </v-icon>
        </v-btn>
      </template>

      <template v-slot:[`footer`]>
        <v-switch
          v-if="$store.getters.userIsAdmin"
          class="pa-0 pl-2 swithInterna"
          style="position: absolute;"
          color="primary"
          label="Exibir Expirados"
          dense
          @change="$emit('switchProcessamentosExpirados', $event)"
        />
      </template>

      <template v-slot:[`footer.page-text`]>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              color="primary"
              v-bind="attrs"
              v-on="on"
              @click="$emit('rechargeTable')"
            >
              mdi-refresh
            </v-icon>
          </template>
          <span>Clique aqui para recarregar o histórico</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <dialog-delete
      :show-dialog="dialogDelete"
      :loading="dialogDeleteLoading"
      @changeValue="dialogDelete = $event"
      @deleteTriggered="excluirLote()"
    >
      <template slot="title">
        Excluir Lote #{{ dialogDeleteData.id }}
      </template>
      <template slot="body">
        Tem certeza que deseja excluir o lote <strong>{{ dialogDeleteData.nome }}</strong>?
      </template>
    </dialog-delete>

    <general-dialog-detalhes
      title="Detalhes do Upload de Log de Unitização"
      :show-dialog="dialogDetalhes"
      max-width="1200px"
      @changeValue="dialogDetalhes = $event"
    >
      <template slot="body">
        <v-progress-linear
          v-if="dialogDetalhesLoading"
          indeterminate
          color="primary"
          class="mb-4"
        />

        <v-data-table
          :headers="resumoHeaders"
          :items="dialogResumoData"
          hide-default-footer
          class="elevation-1 style-table-dialog-detalhes"
          disable-pagination
          mobile-breakpoint="10"
        />
      </template>
    </general-dialog-detalhes>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import LogUnitizacaoService from '@/services/LogUnitizacaoService';

const USUARIOS_IDS_AUTORIZADOS_EXCLUIR_LOTES_TERCEIROS = [
  38,
  53,
  54
];

export default {
  name: 'LogUnitizacaoTabelaUploads',

  components: {
    DialogDelete: () => import('@/components/general/DialogDelete'),
    GeneralProgressBars: () => import('@/components/general/GeneralProgressBars.vue'),
    GeneralStatus: () => import('@/components/general/GeneralStatus.vue'),
    GeneralDialogDetalhes: () => import('@/components/general/GeneralDialogDetalhes')
  },

  props: {
    items: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      search: '',
      downloadProgressData: {},
      dialogDelete: false,
      dialogDeleteLoading: false,
      dialogDeleteData: {
        id: undefined,
        nome: undefined
      },
      dialogDetalhes: false,
      dialogDetalhesLoading: false,
      dialogDetalhesItem: null,
      dialogResumoData: [],
      headers: [
        { text: '#', value: 'id' },
        { text: 'Titulo', value: 'nome' },
        { text: 'Usuario', value: 'usuario_nome' },
        { text: 'Processado em', value: 'processado_em_formatado' },
        { text: 'Status', value: 'status' },
        { text: 'Qtd. itens', value: 'qtd_itens' },
        { text: 'Valor total', value: 'valor_total_formatado' },
        { text: 'Ações', value: 'actions', sortable: false, class: 'pl-4' }
      ],
      resumoHeaders: [
        { text: 'Resumo', value: 'parametro', sortable: false },
        { text: 'Valor', value: 'valor', sortable: false }
      ]
    };
  },

  computed: {
    currentUserId() {
      return this.$store.getters.getUserIdHexa;
    },

    canDeleteOtherUsersLotes() {
      return USUARIOS_IDS_AUTORIZADOS_EXCLUIR_LOTES_TERCEIROS.includes(Number(this.currentUserId));
    },

    itemsFormatados() {
      return this.items.map((item) => ({
        ...item,
        usuario_nome: item.usuario ? item.usuario.nome : '-',
        processado_em_formatado: item.processado_em ? dayjs(item.processado_em).format('DD/MM/YYYY HH:mm') : '-',
        valor_total_formatado: this.formatCurrency(item.valor_total)
      }));
    }
  },

  methods: {
    canDownload(item) {
      return item.status === 'SUCESSO' || item.status === 'FALHA';
    },

    canDelete(item) {
      const usuarioId = item.usuario_id || (item.usuario ? item.usuario.id : null);

      return ['AGUARDANDO', 'FALHA', 'SUCESSO'].includes(item.status)
        && (Number(usuarioId) === Number(this.currentUserId) || this.canDeleteOtherUsersLotes);
    },

    openDialogDelete(item) {
      this.dialogDeleteData = {
        id: item.id,
        nome: item.nome
      };
      this.dialogDeleteLoading = false;
      this.dialogDelete = true;
    },

    async excluirLote() {
      const { id } = this.dialogDeleteData;

      if (!id) {
        return;
      }

      try {
        this.dialogDeleteLoading = true;
        await LogUnitizacaoService.excluirLote(id);
        this.$toast.success('Lote excluído com sucesso.', '', { position: 'topRight' });
        this.$emit('rechargeTable');
      } catch (error) {
        this.$toast.error(this.getApiErrorMessage(error, 'Erro ao excluir lote de Log de Unitização.'), '', { position: 'topRight' });
        console.error(error);
      } finally {
        this.dialogDeleteLoading = false;
        this.dialogDelete = false;
        this.dialogDeleteData = { id: undefined, nome: undefined };
      }
    },

    async downloadItem(item) {
      if (!this.canDownload(item)) {
        return;
      }

      const id = item.id;
      this.$set(this.downloadProgressData, id, { id });
      this.$toast.info('Preparando seu arquivo para download.', '', { position: 'topRight', timeout: 2000 });

      try {
        const request = item.status === 'SUCESSO'
          ? LogUnitizacaoService.downloadResultado(id, {
            timeout: 60 * 60 * 1000,
            onDownloadProgress: (progressEvent) => this.updateDownloadProgress(id, progressEvent)
          })
          : LogUnitizacaoService.downloadRelatorioErros(id, {
            timeout: 60 * 60 * 1000,
            onDownloadProgress: (progressEvent) => this.updateDownloadProgress(id, progressEvent)
          });

        const response = await request;
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');

        link.href = url;
        link.setAttribute('download', this.getFileName(item));
        document.body.appendChild(link);
        link.click();

        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$toast.error(this.getApiErrorMessage(error, 'Falha no download do relatório.'), '', { position: 'topRight' });
        console.error(error);
      } finally {
        this.$delete(this.downloadProgressData, id);
      }
    },

    updateDownloadProgress(id, progressEvent) {
      const progress = progressEvent.total
        ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
        : 0;

      this.$set(this.downloadProgressData, id, {
        id,
        progress,
        isComputable: progressEvent.lengthComputable
      });
    },

    async openDialogDetalhes(item) {
      this.dialogDetalhes = true;
      this.dialogDetalhesLoading = true;
      this.dialogDetalhesItem = item;
      this.dialogResumoData = this.buildResumo(item);

      try {
        const response = await LogUnitizacaoService.getLote(item.id);
        const lote = response.data.data;

        this.dialogDetalhesItem = lote;
        this.dialogResumoData = this.buildResumo(lote);
      } catch (error) {
        this.$toast.error(this.getApiErrorMessage(error, 'Erro ao carregar detalhes do lote.'), '', { position: 'topRight' });
        console.error(error);
      } finally {
        this.dialogDetalhesLoading = false;
      }
    },

    buildResumo(lote) {
      return [
        { parametro: 'ID', valor: lote.id || '-' },
        { parametro: 'TITULO', valor: lote.nome || '-' },
        { parametro: 'USUARIO', valor: lote.usuario ? lote.usuario.nome : (lote.usuario_nome || '-') },
        { parametro: 'STATUS', valor: lote.status || '-' },
        { parametro: 'PROCESSADO EM', valor: lote.processado_em ? dayjs(lote.processado_em).format('DD/MM/YYYY HH:mm') : '-' },
        { parametro: 'CONSULTA', valor: this.getConsultaLabel(lote) },
        { parametro: 'CODIGOS DE OBRA', valor: this.formatCodigosObra(lote) },
        { parametro: 'QTD. ITENS', valor: lote.qtd_itens !== undefined ? lote.qtd_itens : '-' },
        { parametro: 'QTD. ITENS REGISTRADOS', valor: lote.qtd_itens_registrados !== undefined ? lote.qtd_itens_registrados : '-' },
        { parametro: 'QTD. CHAVES', valor: lote.qtd_chaves !== undefined ? lote.qtd_chaves : '-' },
        { parametro: 'QTD. ERROS', valor: lote.qtd_erros !== undefined ? lote.qtd_erros : '-' },
        { parametro: 'VALOR TOTAL', valor: this.formatCurrency(lote.valor_total) },
        { parametro: 'RESUMO', valor: lote.resumo_processamento || '-' }
      ];
    },

    getConsultaLabel(lote) {
      const consulta = lote.logs_unitizacao_consulta || lote.logs_unitizacao_consultas;
      return consulta && consulta.id ? `#${consulta.id}` : '-';
    },

    formatCodigosObra(lote) {
      if (Array.isArray(lote.codigos_obra) && lote.codigos_obra.length) {
        return lote.codigos_obra.join(', ');
      }

      if (lote.codigo_obras) {
        return lote.codigo_obras.split(',').map((codigo) => codigo.trim()).filter(Boolean).join(', ');
      }

      return '-';
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(Number(value || 0));
    },

    getFileName(item) {
      const nome = this.normalizeFileName(item.nome || `lote_${item.id}`);
      return item.status === 'SUCESSO'
        ? `log_unitizacao_${item.id}_${nome}.xlsx`
        : `log_unitizacao_erros_${item.id}_${nome}.xlsx`;
    },

    normalizeFileName(nome) {
      return nome.toString().trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
    },

    getApiErrorMessage(error, fallbackMessage) {
      if (error && error.response && error.response.data && error.response.data.erro) {
        return error.response.data.erro;
      }

      if (error && error.data && error.data.erro) {
        return error.data.erro;
      }

      return fallbackMessage;
    }
  }
};
</script>

<style>
@media screen and (min-width: 800px) {
  .log-unitizacao-table table tr td:nth-child(2) {
    max-width: 220px;
  }

  .log-unitizacao-table table tr td:nth-child(8) {
    white-space: nowrap !important;
  }
}

.swithInterna label {
  font-size: 13px !important;
  margin-left: 0 !important;
}
</style>
